import React, { lazy, Suspense, useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { RestaurantProvider } from "@/contexts/RestaurantContext";
import { CMVRecalculationProvider } from "@/contexts/CMVRecalculationContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { OnboardingGuard } from "@/components/OnboardingGuard";
import ErrorBoundary from "@/components/ErrorBoundary";
import { createIDBPersister } from '@/lib/idbPersister';
import { APP_VERSION } from '@/lib/cacheManager';
import { ROUTES } from '@/config/routes';
const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Lazy load protected routes for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Menu = lazy(() => import("./pages/Menu"));
const Costs = lazy(() => import("./pages/Costs"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const OnboardingWizard = lazy(() => import("./pages/OnboardingWizard"));
const MenuEngineeringReport = lazy(() => import("./pages/MenuEngineeringReport"));

const PriceReviewReport = lazy(() => import("./pages/PriceReviewReport"));
const CMVDiagnosticReport = lazy(() => import("./pages/CMVDiagnosticReport"));
const Stock = lazy(() => import("./pages/Stock"));
const Categories = lazy(() => import("./pages/Categories"));
// Subscription page removed - consolidated into Settings tab
const Checkout = lazy(() => import("./pages/Checkout"));
const AddonCheckout = lazy(() => import("./pages/AddonCheckout"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));

// Sales module pages
const SalesAlaCarte = lazy(() => import("./pages/sales/SalesAlaCarte"));
const SalesRodizio = lazy(() => import("./pages/sales/SalesRodizio"));
const SalesWeight = lazy(() => import("./pages/sales/SalesWeight"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Approval flow pages
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const ApprovalSuccess = lazy(() => import("./pages/ApprovalSuccess"));
const PermissionDenied = lazy(() => import("./pages/PermissionDenied"));
const AccountSuspended = lazy(() => import("./pages/AccountSuspended"));

// Subscription Gate
const SubscriptionGate = lazy(() => import("@/components/subscription/SubscriptionGate").then(m => ({ default: m.SubscriptionGate })));

// Lazy load support pages
const SupportLogin = lazy(() => import("./pages/support/SupportLogin"));
const SupportDashboard = lazy(() => import("./pages/support/SupportDashboard"));
const SupportLayout = lazy(() => import("./pages/support/SupportLayout"));
const SupportClients = lazy(() => import("./pages/support/SupportClients"));
const SupportMetricsPage = lazy(() => import("./pages/support/SupportMetricsPage"));
const SupportNotifications = lazy(() => import("./pages/support/SupportNotifications"));
const SupportAuditLogs = lazy(() => import("./pages/support/SupportAuditLogs"));

const SupportNotificationsMetrics = lazy(() => import("./pages/support/SupportNotificationsMetrics"));
const SupportTickets = lazy(() => import("./pages/support/SupportTickets"));
const SupportApprovals = lazy(() => import("./pages/support/SupportApprovals"));
const SupportCoupons = lazy(() => import("./pages/support/SupportCoupons"));
const SupportReferrals = lazy(() => import("./pages/support/SupportReferrals"));
const SupportRetentionOffers = lazy(() => import("./pages/support/SupportRetentionOffers"));
const ClientDetails = lazy(() => import("@/components/support/ClientDetails").then(m => ({ default: m.ClientDetails })));
const SupportGuard = lazy(() => import("@/components/SupportGuard").then(m => ({ default: m.SupportGuard })));
const OrganizationHub = lazy(() => import("./components/hub/OrganizationHub").then(m => ({ default: m.OrganizationHub })));

// Create persister for React Query cache
const persister = createIDBPersister('alacart-react-query');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Não retry para erros de React internal
        if (error instanceof Error && error.message?.includes('Should have a queue')) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 60000, // 1 minuto de cache padrão (otimizado)
      gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
      refetchOnWindowFocus: false, // Evita re-fetches inesperados
      refetchOnReconnect: 'always', // Sempre recarrega ao reconectar
    },
  },
});

// Register Service Worker (silent in production)
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          if (import.meta.env.DEV) {
            console.log('SW registered:', registration.scope);
          }
        })
        .catch((error) => {
          if (import.meta.env.DEV) {
            console.warn('SW registration failed:', error);
          }
        });
    });
  }
};

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Main App Routes (inside RestaurantProvider with RealtimeProvider)
const MainAppRoutes = () => (
  <RestaurantProvider>
    <RealtimeProvider>
      <Routes>
        <Route path={ROUTES.app.dashboard} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Dashboard />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.inventory} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Inventory />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        {/* Removed orphan routes /entries and /recipes - they were duplicating Dashboard */}
        <Route path={ROUTES.app.stock} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Stock />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.categories} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Categories />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.menu} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Menu />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.costs} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Costs />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.sales.root} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <SalesAlaCarte />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.sales.alacarte} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <SalesAlaCarte />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.sales.rodizio} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <SalesRodizio />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.sales.weight} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <SalesWeight />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.reports.root} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Reports />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.settings} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <Settings />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.reports.menuEngineering} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <MenuEngineeringReport />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.reports.priceReview} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <PriceReviewReport />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.reports.cmvDiagnostic} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <CMVDiagnosticReport />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        {/* Legacy route redirect to new structure */}
        <Route path="/production-by-weight" element={
          <Navigate to={ROUTES.app.sales.weight} replace />
        } />
        {/* Legacy /subscription redirect to settings tab */}
        <Route path="/subscription" element={
          <Navigate to="/settings?tab=subscription" replace />
        } />
        <Route path={ROUTES.app.checkout} element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.addonCheckout} element={
          <ProtectedRoute>
            <AddonCheckout />
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.checkoutSuccess} element={
          <ProtectedRoute>
            <CheckoutSuccess />
          </ProtectedRoute>
        } />
        <Route path={ROUTES.app.organization} element={
          <ProtectedRoute>
            <OnboardingGuard>
              <SubscriptionGate>
                <OrganizationHub />
              </SubscriptionGate>
            </OnboardingGuard>
          </ProtectedRoute>
        } />
        <Route path={ROUTES.onboarding.wizard} element={
          <ProtectedRoute>
            <OnboardingWizard />
          </ProtectedRoute>
        } />
        {/* Legacy redirect for /onboarding to dashboard */}
        <Route path="/onboarding" element={<Navigate to={ROUTES.app.dashboard} replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </RealtimeProvider>
  </RestaurantProvider>
);

const LAST_VERSION_KEY = 'alacart-last-app-version';

const App = () => {
  // Clear corrupted cache when version changes + register service worker
  useEffect(() => {
    const handleVersionChange = async () => {
      const lastVersion = localStorage.getItem(LAST_VERSION_KEY);
      
      if (lastVersion !== APP_VERSION) {
        if (import.meta.env.DEV) {
          console.log(`[App] Version changed from ${lastVersion} to ${APP_VERSION}, clearing caches...`);
        }
        
        // Clear IndexedDB React Query cache
        try {
          const deleteRequest = indexedDB.deleteDatabase('alacart-query-cache');
          deleteRequest.onsuccess = () => {
            if (import.meta.env.DEV) {
              console.log('[App] IndexedDB cache cleared successfully');
            }
          };
        } catch (e) {
          if (import.meta.env.DEV) {
            console.warn('[App] Failed to clear IndexedDB:', e);
          }
        }
        
        // Update stored version
        localStorage.setItem(LAST_VERSION_KEY, APP_VERSION);
      }
    };
    
    handleVersionChange();
    registerServiceWorker();
  }, []);

  return (
    <ErrorBoundary>
        <PersistQueryClientProvider 
          client={queryClient} 
          persistOptions={{ 
            persister,
            maxAge: 24 * 60 * 60 * 1000, // 24 hours - reduced to prevent stale cache issues
            buster: `${APP_VERSION}-v12`, // v12: Removed dead QuickSaleForm, toggle implemented in SaleRegistrationForm
          }}
        >
        <CMVRecalculationProvider>
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <Suspense fallback={<LoadingScreen />}>
                <Routes>
                  {/* Rotas públicas - SEM RestaurantProvider */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  
                  {/* Approval flow routes */}
                  <Route path="/pending-approval" element={<PendingApproval />} />
                  <Route path="/access-denied" element={<AccessDenied />} />
                  <Route path="/approval-success" element={<ApprovalSuccess />} />
                   <Route path="/permission-denied" element={<PermissionDenied />} />
                   <Route path="/account-suspended" element={<AccountSuspended />} />

                  {/* Rotas de suporte - FORA do RestaurantProvider */}
                  <Route path="/internal-support-alc2024">
                    <Route index element={<SupportLogin />} />
                    <Route path="login" element={<SupportLogin />} />
                    <Route element={<SupportGuard><SupportLayout /></SupportGuard>}>
                      <Route path="dashboard" element={<SupportDashboard />} />
                      <Route path="clients" element={<SupportClients />} />
                      <Route path="clients/:id" element={<ClientDetails />} />
                      <Route path="metrics" element={<SupportMetricsPage />} />
                      <Route path="notifications" element={<SupportNotifications />} />
                      
                      <Route path="notifications-metrics" element={<SupportNotificationsMetrics />} />
                      <Route path="tickets" element={<SupportTickets />} />
                      <Route path="audit-logs" element={<SupportAuditLogs />} />
                      <Route path="approvals" element={<SupportApprovals />} />
                      <Route path="coupons" element={<SupportCoupons />} />
                      <Route path="referrals" element={<SupportReferrals />} />
                      <Route path="retention-offers" element={<SupportRetentionOffers />} />
                    </Route>
                  </Route>

                  {/* Rotas do App principal - DENTRO do RestaurantProvider */}
                  <Route path="/*" element={<MainAppRoutes />} />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </CMVRecalculationProvider>
      </PersistQueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
